import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  inject,
  Injector,
  InputSignal,
  input,
  WritableSignal,
  signal,
  InputSignalWithTransform,
  booleanAttribute,
  Signal,
  computed,
  ElementRef,
  viewChild,
  effect,
  untracked,
  AfterViewInit,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  TemplateRef, DestroyRef
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, FormsModule, NgControl, NgForm, ReactiveFormsModule } from '@angular/forms';
import { MAT_FORM_FIELD, MatFormFieldControl } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { IdService } from '../../../services/id.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { Account } from '../../../domain/account.domain';
import { ValueAccessorProviderFactory } from '../../../components/value-accessor/value-accessor-provider-factory';
import { SignalFormBridgeService } from '../../../components/form/interop/signal-form-bridge.service';
import { AccountContextService, StrategyStyleType } from '../services/account-context.service';
import { AccountLabelComponent } from './account-label.component';

@Component({
  selector: 'td-wb-account-selector-material',
  templateUrl: 'account-selector-material.component.html',
  styleUrls: ['account-selector-material.component.scss'],
  providers: [
    ValueAccessorProviderFactory(AccountSelectorMaterialComponent),
    {
      provide: MatFormFieldControl,
      useExisting: AccountSelectorMaterialComponent
    },
    TranslatePipe
  ],
  imports: [
    MatSelectModule,
    TranslateDirective,
    FormsModule,
    NgTemplateOutlet,
    AccountLabelComponent,
    ReactiveFormsModule
  ],
  host: {
    '[attr.id]': 'id',
    '[class.td-wb-account-selector__floating-label]': 'focused || !empty'
  }
})
export class AccountSelectorMaterialComponent implements ControlValueAccessor, MatFormFieldControl<Account>, OnInit, AfterViewInit, OnDestroy {

  private static readonly ACCOUNT_SELECTOR_ID_PREFIX: string = 'td-wb-account-selector';
  private static readonly DROPDOWN_ID_PREFIX: string = 'td-wb-account-selector-dropdown';
  readonly accountDropdown: Signal<MatSelect> = viewChild.required<MatSelect>('accountDropdown');
  readonly stateChanges: Subject<void> = new Subject<void>();
  readonly _userAriaDescribedBy: InputSignal<string> = input<string>('', { alias: 'aria-describedby' });
  private readonly idService = inject(IdService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly bridgeService = inject(SignalFormBridgeService);
  private readonly injector: Injector = inject(Injector);
  private readonly accountContextService = inject(AccountContextService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly parentForm = inject(NgForm, { optional: true });
  private readonly parentFormGroup = inject(FormGroupDirective, { optional: true });

  readonly _placeholder: InputSignal<string> = input<string>(this.translatePipe.transform(
    'tdWb.components.accountSelector.ACCOUNT_PLACEHOLDER'), { alias: 'placeholder' });

  readonly _required: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'required',
    transform: booleanAttribute
  });

  readonly _disabledByInput: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'disabled',
    transform: booleanAttribute
  });

  protected readonly _formField = inject(MAT_FORM_FIELD, {
    optional: true
  });

  readonly controlType: string = 'mat-select';
  private readonly _focused: WritableSignal<boolean> = signal(false);
  private readonly _disabled: Signal<boolean> = computed(() => this._disabledByInput() || this._disabledByCva());
  private readonly _elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  dropdownId: string;
  readonly describedbyIds: WritableSignal<string | null> = signal(null);

  readonly accounts: InputSignal<Array<Account>> = input<Array<Account>>([]);

  readonly accountContext: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'accountContext',
    transform: booleanAttribute
  });

  readonly accountContextUpdate: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'accountContextUpdate',
    transform: booleanAttribute
  });

  readonly accountContextGroupOverride: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
    alias: 'accountContextGroupOverride',
    transform: booleanAttribute
  });

  readonly accountContextType: InputSignal<StrategyStyleType> = input<StrategyStyleType>('default');
  readonly initAccountId: InputSignal<string> = input<string>(undefined);
  readonly _id: InputSignal<string> = input<string>(undefined, { alias: 'id' });
  readonly showErrorBeforeSubmit: InputSignal<boolean> = input<boolean>(true);
  readonly accountLabelTemplate: InputSignal<TemplateRef<unknown>> = input<TemplateRef<unknown>>(undefined);

  accountFormControl: FormControl<Account | null>;

  /** Single source of truth. Written by `writeValue()` (no echo) and the sync below. */
  private readonly _value: WritableSignal<Account | null> = signal(null);
  private readonly _disabledByCva: WritableSignal<boolean> = signal(false);
  private onChangeCallback: (value: Account | null) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};
  private ngControlRef: NgControl | null = null;
  private isNgControlResolved: boolean = false;
  private isUsingFallbackControl: boolean = false;

  get value(): Account | null {
    return this._value();
  }

  /** Required by `MatFormFieldControl`. Resolved lazily — see `resolveNgControl()`. */
  get ngControl(): NgControl | null {
    return this.resolveNgControl();
  }

  get userAriaDescribedBy(): string {
    return this._userAriaDescribedBy();
  }

  get placeholder(): string {
    return this._placeholder();
  }

  get focused(): boolean {
    return this._focused();
  }

  get empty(): boolean {
    return !this.accountFormControl.value;
  }

  get required(): boolean {
    return this._required();
  }

  get disabled(): boolean {
    return this._disabled();
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    const parent = this.parentForm || this.parentFormGroup;
    const hasBeenTouched = !!this.accountFormControl?.touched;
    const shouldShowErrors =
      (hasBeenTouched && this.showErrorBeforeSubmit()) || !!parent?.submitted;
    return this.accountFormControl?.invalid && shouldShowErrors;
  }

  id: string;
  constructor() {
    effect(() => {
      // Read signals to trigger effect.
      this._placeholder();
      this._required();
      this._disabled();
      this._focused();
      // Propagate state changes.
      untracked(() => this.stateChanges.next());
    });

    effect(() => {
      if (this._disabled()) {
        untracked(() => this.accountFormControl?.disable());
      } else {
        untracked(() => this.accountFormControl?.enable());
      }
    });
  }

  ngOnInit(): void {
    this.id = this._id() ?? this.idService.generateNamespaceSequentialId(AccountSelectorMaterialComponent.ACCOUNT_SELECTOR_ID_PREFIX);
    this.dropdownId = this.idService.generateNamespaceSequentialId(AccountSelectorMaterialComponent.DROPDOWN_ID_PREFIX);
    this.attachToOuterControl();

    if (this.accountContext()) {
      this.restoreAccountContext();
    }
  }

  ngAfterViewInit(): void {
    // Retry: under reactive forms (`formControlName`), `FormControlName.control` is
    // sometimes not yet linked when our own `ngOnInit()` runs, so `attachToOuterControl()`
    // falls back to a standalone control. By `ngAfterViewInit`, the whole initial view
    // (including all sibling form directives on this host element) has finished
    // initializing, so the real control should now be resolvable.
    if (!this.isUsingFallbackControl) {
      return;
    }

    const mutableOuterControl = this.bridgeService.getMutableControl(this.resolveNgControl()) as FormControl<Account | null> | null;
    if (mutableOuterControl === null) {
      return;
    }

    const pendingValue = this.accountFormControl.value;
    this.isUsingFallbackControl = false;
    this.accountFormControl = mutableOuterControl;
    this.wireAccountFormControl();
    if (pendingValue !== null && this.accountFormControl.value === null) {
      // Must emit (the default): this transplants a value the control already resolved for
      // itself (context restoration), not an owner write, so it must notify the same way a
      // user-driven selection would. `wireAccountFormControl()` was just called above, so its
      // `valueChanges` subscription is already active and will run `writeValue()` for us -
      // see the comment on `restoreAccountContext()`.
      this.accountFormControl.setValue(pendingValue);
    } else if (this.accountFormControl.value === null && this.accountContext()) {
      // `pendingValue` itself can be stale: Angular's own `writeValue(control.value)` call
      // when it finally links the *real* control here races with (and, per `writeValue()`'s
      // fallback-control sync, silently clobbers) the value `restoreAccountContext()` wrote
      // into the temporary fallback control during `ngOnInit()`. Re-resolve directly against
      // the now-linked real control instead of trusting that snapshot.
      this.restoreAccountContext();
    }
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();

  }

  setDescribedByIds(ids: Array<string>): void {
    this.describedbyIds.set(ids.join(' '));
  }

  onContainerClick(event: MouseEvent): void {
    // Most of this code was taken from Angular Material's MatSelect implementation of onContainerClick
    // Need the below assertion to ensure classList and closest are identified as valid properties/methods
    const target = (event?.target) as HTMLElement | null;

    // Since the overlay is inside the form field, this handler can fire for interactions
    // with the container. Note that while it's redundant to select both for the popover
    // and select panel, we need to do it because it tests the clicks can occur after
    // the panel was detached from the popover.
    if (
      target &&
      (target.tagName === 'MAT-OPTION' ||
        target.classList.contains('cdk-overlay-backdrop') ||
        target.closest('.mat-mdc-select-panel'))
    ) {
      return;
    }
    this.accountDropdown().focus();
    this.accountDropdown().open();
  }

  onFocusIn(): void {
    if (!this._focused()) {
      this._focused.set(true);
    }
  }

  onFocusOut(event: FocusEvent): void {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this._focused.set(false);
      this.onTouchedCallback();
    }
  }

  private storeAccountAndGroupId(account: Account): void {
    if (this.accountContextUpdate() && account && account.id !== undefined && account.groupId !== undefined) {
      this.accountContextService.setSelectedAccountId(account.id, this.accountContextType());
      this.accountContextService.setSelectedAccountGroupId(account.groupId);
    }
  }

  /**
   * Owner -> control. Must not echo through `onChangeCallback` (§4.2 criterion 4).
   *
   * When `accountFormControl` IS the outer control (classic forms), it is already up to
   * date — the owner just wrote directly to the same object. When it is the standalone
   * fallback (Signal Forms, or no NgControl at all), nothing else pushes the value into the
   * control that actually drives `<mat-select [formControl]>`, so it must be done here.
   */
  writeValue(value: Account | null): void {
    this._value.set(value ?? null);
    if (this.isUsingFallbackControl && this.accountFormControl && this.accountFormControl.value !== (value ?? null)) {
      this.accountFormControl.setValue(value ?? null, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: Account | null) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
  }

  /**
   * Resolved lazily: injecting NgControl eagerly while this component is also registered as
   * its own NG_VALUE_ACCESSOR creates an NG0200 circular dependency under `[formField]`.
   */
  private resolveNgControl(): NgControl | null {
    if (!this.isNgControlResolved) {
      this.ngControlRef = this.bridgeService.getNgControl(this.injector);
      this.isNgControlResolved = true;
    }

    return this.ngControlRef;
  }

  /**
   * Reuse the owning control when it is a real, mutable `AbstractControl` (classic Angular
   * Forms), so `errorState`/touched/validity naturally reflect the parent form. Under Signal
   * Forms, `ngControl?.control` is the `InteropNgControl` shim (`control = this`), which
   * implements neither `setValue` nor `markAsTouched` — binding `<mat-select [formControl]>`
   * to it directly would throw. `getMutableControl()` filters the shim out, so
   * `accountFormControl` is ALWAYS a genuine, safely-mutable `FormControl` (see hazard H2).
   * Falls back to a standalone control when no real one is available yet — retried from
   * `ngAfterViewInit()`.
   */
  private attachToOuterControl(): void {
    const mutableOuterControl = this.bridgeService.getMutableControl(this.resolveNgControl()) as FormControl<Account | null> | null;
    this.isUsingFallbackControl = mutableOuterControl === null;
    this.accountFormControl = mutableOuterControl ?? new FormControl<Account | null>(null);
    this.wireAccountFormControl();
  }

  /**
   * Resolves and applies the account-context service's currently selected account. Called
   * from `ngOnInit()` (the common case) and, again, from `ngAfterViewInit()` when the initial
   * call landed on a fallback control whose value was subsequently clobbered by Angular's own
   * `writeValue(control.value)` call racing in after the *real* control finally linked.
   */
  private restoreAccountContext(): void {
    this.accountContextService.initSelectedAccount(this.accounts(), this.accountContextType(), this.accountContextUpdate(),
      this.initAccountId(), this.accountContextGroupOverride())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((account: Account): void => {
        if (account !== undefined) {
          // Must emit (the default), not suppress: this is the control's own initiation of a
          // value - conceptually a system-driven selection, not an owner write into the control
          // - so it has to notify the same way a user picking an option from the dropdown does.
          // When `accountFormControl` IS the shared outer control (the common `formControlName`
          // case), `wireAccountFormControl()`'s own `valueChanges` subscription (already active
          // by now - it is wired in `ngOnInit()` before this is ever called) picks this up and
          // runs `writeValue()` / the fallback-only `onChangeCallback()` / `storeAccountAndGroupId()`
          // for us, exactly as it does for a user-driven selection. Because it is the same
          // control object, the owning form's own `valueChanges` subscribers (e.g. order entry's
          // `accountSelector` subscription driving `selectedTradingAccountChange`) are notified
          // too - restoring an account from context on initial load must look identical, from
          // the owner's perspective, to the user picking it from the dropdown. Suppressing the
          // event here silently breaks every consumer downstream of that subscription while
          // leaving the dropdown itself looking correct (Angular's internal model-change
          // pipeline updates bound directives regardless of `emitEvent`), which is what made
          // this defect easy to miss.
          this.accountFormControl.setValue(account);
          this.accountFormControl.markAsTouched();
          // This is needed to avoid ExpressionChangedAfterItHasBeenCheckedError
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private wireAccountFormControl(): void {
    this.accountFormControl?.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
      this.stateChanges.next();
    });

    this.accountFormControl?.valueChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
      // `writeValue()` alone does not notify the owner (correctly — owner writes must not
      // echo). When `accountFormControl` IS the outer control (classic forms), the owner is
      // already updated because they share the same object — calling `onChangeCallback` in
      // that case would loop back through the owner's OWN registered accessor callback and
      // recurse indefinitely. Only the standalone fallback (Signal Forms, or no NgControl at
      // all) needs the explicit CVA notification.
      this.writeValue(this.accountFormControl.value ?? null);
      if (this.isUsingFallbackControl) {
        this.onChangeCallback(this.accountFormControl.value ?? null);
      }
      this.storeAccountAndGroupId(this.accountFormControl.value);
    });
  }
}
